import React, { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import Page from "../components/Page";
import {
  IconReceipt2,
  IconSearch,
  IconCalendar,
  IconFilter,
  IconDownload,
  IconUser,
  IconX,
  IconReceiptOff
} from "@tabler/icons-react";
import { iconStroke } from "../config/config";
import { useInvoices } from "../controllers/invoices.controller";
import { CURRENCIES } from "../config/currencies.config";
import { toast } from "react-hot-toast";
import ReportExportWrapper from "../components/ReportExportWrapper";

export default function VoidedInvoicesReportPage() {
  const { t } = useTranslation();
  const { theme } = useTheme();

  const filters = [
    { key: "today", value: t("reports.filters.today") },
    { key: "yesterday", value: t("reports.filters.yesterday") },
    { key: "last_7days", value: t("reports.filters.last_7days") },
    { key: "this_month", value: t("reports.filters.this_month") },
    { key: "last_month", value: t("reports.filters.last_month") },
    { key: "custom", value: t("reports.filters.custom") },
  ];

  const [state, setState] = useState({
    filter: filters[3].key,
    fromDate: null,
    toDate: null,
    search: "",
  });

  const fromDateRef = useRef();
  const toDateRef = useRef();
  const filterTypeRef = useRef();

  const { data, isLoading, error } = useInvoices({
    type: state.filter,
    from: state.fromDate,
    to: state.toDate,
  });

  if (isLoading) return <Page><div className="flex items-center justify-center h-full"><span className="loading loading-spinner loading-lg text-restro-green"></span></div></Page>;

  const voidedInvoices = (data || []).filter(inv => inv.invoice_payment_status === 'voided');

  // filter by search
  const filteredInvoices = voidedInvoices.filter(inv => 
    inv.invoice_id.toString().includes(state.search) ||
    (inv.name || "").toLowerCase().includes(state.search.toLowerCase())
  );

  const currency = CURRENCIES.find((c) => c.cc === "USD")?.symbol || "$"; // fallback

  const columns = [
    { header: "Invoice ID", key: "invoice_id" },
    { header: "Date", key: "date" },
    { header: "Customer", key: "customer" },
    { header: "Amount", key: "amount" },
    { header: "Void Reason", key: "void_reason" }
  ];

  const tableData = filteredInvoices.map(inv => ({
    invoice_id: `#${inv.invoice_id}`,
    date: new Date(inv.created_at).toLocaleString(),
    customer: inv.name || "Walk-in",
    amount: currency + inv.total,
    void_reason: inv.void_reason || "No reason provided"
  }));

  const summaryData = {
    "Total Voided Invoices": filteredInvoices.length,
    "Total Voided Amount": currency + filteredInvoices.reduce((sum, inv) => sum + parseFloat(inv.total || 0), 0).toFixed(2)
  };

  return (
    <Page>
      <ReportExportWrapper
        title="Voided Invoices Report"
        reportType="Invoices & Auditing"
        dateRange={filters.find(f => f.key === state.filter)?.value}
        filters={`Search: ${state.search || 'None'}`}
        columns={columns}
        tableData={tableData}
        summaryData={summaryData}
      >
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-8 gap-4 print:hidden">
          <div>
            <h3 className="text-3xl font-black tracking-tight flex items-center gap-2 text-red-600">
              <IconReceiptOff size={32} /> Voided Invoices Report
            </h3>
            <p className="text-gray-500 text-sm mt-1">Audit trail of cancelled transactions and their reasons</p>
          </div>

          <div className="flex gap-2">
              <button
                  onClick={() => document.getElementById("filter-dialog").showModal()}
                  className="btn btn-ghost bg-restro-gray border border-restro-border-green rounded-2xl gap-2"
              >
                  <IconFilter stroke={iconStroke} size={20} /> {t("reports.filters_button")}
              </button>
          </div>
        </div>

      <div className="flex mb-6 bg-white dark:bg-restro-gray p-2 rounded-3xl border border-gray-100 dark:border-gray-800 shadow-sm max-w-md">
        <div className="flex items-center px-4 text-gray-400">
            <IconSearch size={20} />
        </div>
        <input 
            type="text" 
            placeholder="Search by ID or customer..." 
            className="flex-1 bg-transparent border-none focus:ring-0 text-sm py-3 outline-none"
            value={state.search}
            onChange={(e) => setState({...state, search: e.target.value})}
        />
      </div>

      <div className="bg-white dark:bg-restro-gray border border-gray-100 dark:border-gray-800 rounded-[2rem] overflow-hidden shadow-sm">
        <div className="overflow-x-auto print:overflow-visible">
          <table className="table table-zebra w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/50 print:bg-transparent border-b">
              <tr>
                <th className="font-bold text-xs uppercase tracking-widest p-6 text-gray-400 print:text-black print:p-2">Invoice ID</th>
                <th className="font-bold text-xs uppercase tracking-widest p-6 text-gray-400 print:text-black print:p-2">Date</th>
                <th className="font-bold text-xs uppercase tracking-widest p-6 text-gray-400 print:text-black print:p-2">Customer</th>
                <th className="font-bold text-xs uppercase tracking-widest p-6 text-gray-400 print:text-black print:p-2">Amount</th>
                <th className="font-bold text-xs uppercase tracking-widest p-6 text-gray-400 print:text-black print:p-2">Void Reason</th>
              </tr>
            </thead>
            <tbody>
              {filteredInvoices.length === 0 && (
                <tr>
                  <td colSpan="5" className="text-center py-20 opacity-50 italic">No voided invoices found for this period.</td>
                </tr>
              )}
              {filteredInvoices.map((inv) => (
                <tr key={inv.invoice_id} className="hover:bg-gray-50/50 dark:hover:bg-gray-800/50 transition print:hover:bg-transparent">
                  <td className="p-6 font-bold text-red-600 print:text-black print:p-2">#{inv.invoice_id}</td>
                  <td className="p-6 text-sm text-gray-500 print:text-black print:p-2">{new Date(inv.created_at).toLocaleString()}</td>
                  <td className="p-6 print:p-2">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-restro-gray flex items-center justify-center text-xs font-bold text-gray-400 uppercase print:hidden">
                            {inv.name?.[0] || <IconUser size={14}/>}
                        </div>
                        <span className="font-medium text-sm">{inv.name || "Walk-in"}</span>
                    </div>
                  </td>
                  <td className="p-6 font-black print:p-2">{currency}{inv.total}</td>
                  <td className="p-6 print:p-2">
                    <div className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 p-3 rounded-2xl text-xs border border-red-100 dark:border-red-900/30 max-w-xs italic print:bg-transparent print:border-none print:text-black print:p-0">
                        "{inv.void_reason || "No reason provided"}"
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* filter dialog */}
      <dialog id="filter-dialog" className="modal">
        <div className = 'modal-box border border-restro-border-green dark:rounded-3xl p-0 overflow-hidden'>
          <div className="bg-gray-900 p-8 text-white">
            <h3 className = "font-black text-2xl flex items-center gap-2">
                <IconFilter stroke={2} /> {t("reports.filter")}
            </h3>
            <p className="text-gray-400 text-sm mt-1">Select date range for report</p>
          </div>

          <div className="p-8 space-y-6">
            <div>
              <label className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-2">{t("reports.filter_label")}</label>
              <select
                className='select select-bordered w-full rounded-2xl bg-gray-50 dark:bg-gray-900 border-none'
                ref={filterTypeRef}
                defaultValue={state.filter}
              >
                {filters.map((filter, index) => (
                  <option key={index} value={filter.key}>
                    {filter.value}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="form-control">
                <label className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-2">{t("reports.from")}</label>
                <input
                  type="date"
                  ref={fromDateRef}
                  className='bg-gray-50 dark:bg-gray-900 border-none rounded-2xl py-3 px-4 font-bold text-sm'
                />
              </div>
              <div className="form-control">
                <label className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-2">{t("reports.to")}</label>
                <input
                  type="date"
                  ref={toDateRef}
                  className='bg-gray-50 dark:bg-gray-900 border-none rounded-2xl py-3 px-4 font-bold text-sm'
                />
              </div>
            </div>

            <div className="flex gap-4 pt-4">
              <button onClick={() => document.getElementById("filter-dialog").close()} className="flex-1 py-4 font-bold text-gray-500 bg-gray-100 dark:bg-gray-800 rounded-2xl transition active:scale-95">{t("reports.close")}</button>
              <button
                onClick={() => {
                  setState({
                    ...state,
                    filter: filterTypeRef.current.value,
                    fromDate: fromDateRef.current.value || null,
                    toDate: toDateRef.current.value || null,
                  });
                  document.getElementById("filter-dialog").close();
                }}
                className = 'flex-[1.5] py-4 font-bold text-white bg-restro-green rounded-2xl hover:bg-restro-green-button-hover transition active:scale-95 shadow-lg shadow-green-100 dark:shadow-none'
              >
                {t("reports.apply")}
              </button>
            </div>
          </div>
        </div>
      </dialog>
      </ReportExportWrapper>
    </Page>
  );
}
